import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import { Helmet } from "react-helmet-async";
import { GradFlow } from "gradflow";

import HeroBubbles from "../components/HeroBubbles";
import { HERO_GRADFLOW_CONFIG } from "../utils/heroGradient";

const getOrCreateWebDeviceId = () => {
  let id = localStorage.getItem("bleepWebDeviceId");
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem("bleepWebDeviceId", id);
  }
  return id;
};

const getWebDeviceInfo = () => {
  const ua = navigator.userAgent;

  let browser = "Unknown";
  if (/Edg\//.test(ua)) browser = "Edge";
  else if (/OPR\/|Opera/.test(ua)) browser = "Opera";
  else if (/Chrome\//.test(ua)) browser = "Chrome";
  else if (/Firefox\//.test(ua)) browser = "Firefox";
  else if (/Safari\//.test(ua)) browser = "Safari";

  const versionKey = browser === "Edge" ? "Edg" : browser;
  const vMatch = ua.match(new RegExp(`${versionKey}\\/(\\d+)`));
  const model = vMatch ? `${browser} ${vMatch[1]}` : browser;

  let brand = "Unknown";
  if (/Windows/.test(ua)) brand = "Windows";
  else if (/Mac OS X/.test(ua)) brand = "macOS";
  else if (/Android/.test(ua)) brand = "Android";
  else if (/iPhone|iPad/.test(ua)) brand = "iOS";
  else if (/Linux/.test(ua)) brand = "Linux";

  return {
    model,
    brand,
    metadata: {
      userAgent: ua,
      screenResolution: `${window.screen.width}x${window.screen.height}`,
      language: navigator.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    },
  };
};

const registerWebDevice = (userId) => {
  const deviceId = getOrCreateWebDeviceId();
  const { model, brand, metadata } = getWebDeviceInfo();
  fetch(`/api/users/${userId}/devices/heartbeat`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ deviceId, os: "web", model, brand, metadata }),
  }).catch(() => {});
};

const OTP_LENGTH = 6;

const SignIn = ({ handleSignIn }) => {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const inputs = useRef([]);

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [invalidEmail, setInvalidEmail] = useState(false);
  const [invalidAuth, setInvalidAuth] = useState(false);
  const [invalidPassword, setInvalidPassword] = useState(false);
  const [invalidPhone, setInvalidPhone] = useState(false);
  const [regularUser, setRegularUser] = useState(true);
  const [loading, setLoading] = useState(false);
  const [verificationStep, setVerificationStep] = useState(0);
  const [otp, setOTP] = useState(Array(OTP_LENGTH).fill(""));
  const [medium, setMedium] = useState("email");
  const [showPassword, setShowPassword] = useState(false);

  /* ---------------- Focus follows the step -------------------------------- */
  useEffect(() => {
    const t = setTimeout(() => {
      if (verificationStep === 0) inputRef.current?.focus();
      else if (regularUser) inputs.current[0]?.focus();
    }, 300);
    return () => clearTimeout(t);
  }, [verificationStep, regularUser]);

  /* ---------------- OTP boxes --------------------------------------------- */

  // Commits a whole set of boxes at once and submits if that completed the
  // code. The code is handed to authenticateUser explicitly: setOTP has not
  // flushed at this point, so reading `otp` back would submit the previous
  // value.
  const commitOTP = (next) => {
    setOTP(next);

    const filled = next.filter(Boolean).length;
    if (filled === OTP_LENGTH) {
      inputs.current[OTP_LENGTH - 1]?.focus();
      authenticateUser(next.join(""));
    } else {
      inputs.current[Math.min(filled, OTP_LENGTH - 1)]?.focus();
    }
  };

  const handleInputChange = (index, value) => {
    const chars = value.toUpperCase().replace(/\s+/g, "").split("");

    // maxLength stops a person typing more than one character, but iOS SMS
    // autofill writes the whole code into the first box, so a multi-character
    // change is spread across the boxes the same way a paste is.
    if (chars.length > 1) {
      const next = [...otp];
      chars.forEach((char, i) => {
        if (index + i < OTP_LENGTH) next[index + i] = char;
      });
      commitOTP(next);
      return;
    }

    const next = [...otp];
    next[index] = chars[0] || "";
    setOTP(next);
    if (next[index] && index < OTP_LENGTH - 1) {
      inputs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (event, index) => {
    if (event.key === "Backspace" && index > 0 && !otp[index]) {
      const newOTP = [...otp];
      newOTP[index - 1] = "";
      setOTP(newOTP);
      inputs.current[index - 1].focus();
    }
  };

  // A pasted code always fills from the first box, whichever box received the
  // paste. Whitespace is stripped first: a code copied out of an email usually
  // arrives with a trailing newline or surrounding spaces.
  const handlePaste = (event) => {
    event.preventDefault();
    const chars = event.clipboardData
      .getData("Text")
      .toUpperCase()
      .replace(/\s+/g, "")
      .split("");

    const next = Array(OTP_LENGTH).fill("");
    chars.forEach((char, index) => {
      if (index < OTP_LENGTH) next[index] = char;
    });
    commitOTP(next);
  };

  /* ---------------- Step 0: send the code --------------------------------- */
  const authenticateEmailorPhone = () => {
    if (username) {
      if (loading) return;
      setLoading(true);

      const isEmail = (input) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input);
      const isPhoneNumber = (input) => {
        const cleanedInput = input.replace(/\s+/g, "");
        const phoneRegex = /^\+?\d{10,15}$/;
        return phoneRegex.test(cleanedInput);
      };

      const createPayload = (value) => {
        if (isEmail(value)) {
          setMedium("email");
          return JSON.stringify({ email: value });
        }
        if (isPhoneNumber(value)) {
          setMedium("phone");
          return JSON.stringify({ phone: value });
        }
        setInvalidEmail(true);
        return JSON.stringify({ email: "" });
      };

      const myHeaders = new Headers();
      myHeaders.append("Content-Type", "application/json");

      const raw = createPayload(username);

      if (raw === JSON.stringify({ email: "" })) {
        setLoading(false);
        return;
      }

      const requestOptions = {
        method: "PUT",
        headers: myHeaders,
        body: raw,
        redirect: "follow",
      };

      fetch("/api/users/sendverification", requestOptions)
        .then((response) => response.json())
        .then((result) => {
          setInvalidEmail(false);
          setInvalidPhone(false);
          setInvalidAuth(false);
          setInvalidPassword(false);
          setLoading(false);
          if (result.message === "Escalated user") {
            setRegularUser(false);
            setVerificationStep(verificationStep + 1);
          } else {
            setRegularUser(true);
          }

          if (result.message === "Verification SMS sent.") {
            setInvalidPhone(false);
            setVerificationStep(verificationStep + 1);
          }

          if (
            result.message === "Invalid phone number." ||
            result.message === "Invalid or unsupported carrier."
          ) {
            setInvalidPhone(true);
          }

          if (result.message === "Verification email sent.") {
            setInvalidEmail(false);
            setInvalidPhone(false);
            setVerificationStep(verificationStep + 1);
          }
        })
        .catch((error) => {
          setInvalidEmail(true);
          setLoading(false);
          console.error("error", error);
        });
    } else {
      setInvalidEmail(true);
    }
  };

  /* ---------------- Step 1: code, or password for staff ------------------- */
  // `code` is passed in by commitOTP, which submits before its setOTP has
  // flushed. Everything else falls back to what is in state.
  const authenticateUser = (code = otp.join("")) => {
    if (loading) return;
    setLoading(true);

    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");

    // Honours ?redirect=, which is how the report FAB sends a signed-out user
    // here and gets them back to the form they were opening.
    const goAfterSignIn = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const redirect = urlParams.get("redirect");
      if (redirect) {
        // "report" used to mean "go home and pop the modal open". It is a page
        // now, so it is just a route.
        navigate(redirect === "report" ? "/report" : `${redirect}`);
      } else {
        navigate("/");
      }
    };

    if (regularUser) {
      const raw = JSON.stringify({
        emailOrPhone: username,
        verificationCode: code,
      });

      fetch("/api/users/authenticateverification", {
        method: "PUT",
        headers: myHeaders,
        body: raw,
        redirect: "follow",
      })
        .then((response) => {
          if (!response.ok) throw new Error("Authentication failed");
          return response.json();
        })
        .then((data) => {
          setInvalidAuth(false);

          Cookies.set("userId", data.user._id, { expires: 7 });
          Cookies.set("userType", data.user.type, { expires: 7 });
          Cookies.set("userName", data.user.username, { expires: 7 });
          if (data.user.email)
            Cookies.set("userEmail", data.user.email, { expires: 7 });
          Cookies.set("userPhone", data.user.phone, { expires: 7 });
          Cookies.set("userAnonymous", data.user.anonymous, { expires: 7 });
          registerWebDevice(data.user._id);
          handleSignIn();
          goAfterSignIn();
        })
        .catch(() => {
          setInvalidAuth(true);
          setLoading(false);
          // Clear and refocus rather than leaving six wrong characters for the
          // person to delete one at a time. This matters more now that a paste
          // submits on its own.
          setOTP(Array(OTP_LENGTH).fill(""));
          inputs.current[0]?.focus();
        });
    } else {
      const raw = JSON.stringify({ username, password });

      fetch("/api/users/authenticateadmin", {
        method: "PUT",
        headers: myHeaders,
        body: raw,
        redirect: "follow",
      })
        .then((response) => {
          if (!response.ok) throw new Error("Authentication failed");
          return response.json();
        })
        .then((data) => {
          setInvalidPassword(false);

          Cookies.set("userId", data.user._id, { expires: 1 });
          Cookies.set("userType", data.user.type, { expires: 1 });
          Cookies.set("userName", data.user.username, { expires: 1 });
          Cookies.set("userEmail", data.user.email, { expires: 1 });
          Cookies.set("userPassword", password, { expires: 1 });
          Cookies.set("userOrg", data.user.organization, { expires: 1 });
          Cookies.set(
            "userPermissions",
            JSON.stringify(data.user.permissions || {}),
            { expires: 1 },
          );
          Cookies.set("userAnonymous", data.user.anonymous, { expires: 1 });
          registerWebDevice(data.user._id);
          handleSignIn();
          if (data.user.type === "user") {
            goAfterSignIn();
          } else {
            navigate("/dashboard");
          }
        })
        .catch(() => {
          setInvalidPassword(true);
          setLoading(false);
        });
    }
  };

  // A real submit handler rather than a document-level keydown listener. The
  // old listener was rebound on [username, password, otp] but closed over
  // `verificationStep`, `loading` and `regularUser`, so Enter could run the
  // wrong step's handler or fire a second request while one was in flight.
  const proceed = (e) => {
    e?.preventDefault();
    if (verificationStep === 0) authenticateEmailorPhone();
    else authenticateUser();
  };

  const back = () => {
    setVerificationStep(0);
    setOTP(Array(OTP_LENGTH).fill(""));
    setPassword("");
    setInvalidAuth(false);
    setInvalidPassword(false);
  };

  const heading =
    verificationStep === 0
      ? "Sign in"
      : regularUser
        ? "Check your inbox"
        : "Welcome back";

  return (
    <div className="bleep-light signin-layout">
      <Helmet>
        <title>Sign-In | BLEEP</title>
        <meta
          name="description"
          content="Sign in to BLEEP to report suspected counterfeit products and follow what happens next."
        />
      </Helmet>

      {/* ------------------------------------------------------- Form column */}
      <div className="signin-form-col">
        <div className="signin-inner">
          <div className="signin-top">
            <Link
              to="/"
              aria-label="BLEEP home"
            >
              <img
                src="/images/bleeplogo_primary.png"
                alt="BLEEP"
                className="signin-logo"
              />
            </Link>
            {/* was an <a> with no href, so it was unreachable by keyboard */}
            <button
              type="button"
              className="bl-close-btn"
              onClick={() => navigate(-1)}
              aria-label="Go back"
            >
              <i
                className="fas fa-xmark"
                aria-hidden="true"
              />
            </button>
          </div>

          <h1 className="signin-title">{heading}</h1>

          <form
            className="signin-form"
            onSubmit={proceed}
            noValidate
          >
            {verificationStep === 0 && (
              <>
                <p className="signin-lead">
                  Enter your email address or phone number and we will send you
                  a verification code.
                </p>

                {invalidEmail && (
                  <div
                    className="bl-alert bl-alert--danger"
                    role="alert"
                  >
                    <i
                      className="bl-alert__icon fas fa-circle-exclamation"
                      aria-hidden="true"
                    />
                    <div>Enter a valid email address or phone number.</div>
                  </div>
                )}

                {invalidPhone && (
                  <div
                    className="bl-alert bl-alert--danger"
                    role="alert"
                  >
                    <i
                      className="bl-alert__icon fas fa-circle-exclamation"
                      aria-hidden="true"
                    />
                    <div>
                      Unsupported phone number. We currently support Airtel and
                      MTN networks.
                    </div>
                  </div>
                )}

                <div className="bl-field">
                  <label
                    className="bl-field__label"
                    htmlFor="signin-username"
                  >
                    Email or phone number
                  </label>
                  <input
                    id="signin-username"
                    ref={inputRef}
                    className="bl-input"
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    autoComplete="username"
                    required
                    aria-required="true"
                  />
                </div>
              </>
            )}

            {verificationStep === 1 && regularUser && (
              <>
                <p className="signin-lead">
                  We sent a 6 digit code to your {medium}{" "}
                  <strong>{username}</strong>. Cannot find it? Check your spam
                  folder.
                </p>

                {invalidAuth && (
                  <div
                    className="bl-alert bl-alert--danger"
                    role="alert"
                  >
                    <i
                      className="bl-alert__icon fas fa-circle-exclamation"
                      aria-hidden="true"
                    />
                    <div>That code is not correct. Check it and try again.</div>
                  </div>
                )}

                <div className="bl-field">
                  <span
                    className="bl-field__label"
                    id="signin-otp-label"
                  >
                    Verification code
                  </span>
                  <div
                    className="signin-otp"
                    role="group"
                    aria-labelledby="signin-otp-label"
                  >
                    {Array.from({ length: OTP_LENGTH }, (_, index) => (
                      <input
                        key={index}
                        ref={(input) => (inputs.current[index] = input)}
                        className="signin-otp__box"
                        type="text"
                        maxLength="1"
                        value={otp[index]}
                        onChange={(e) =>
                          handleInputChange(index, e.target.value)
                        }
                        onKeyDown={(e) => handleKeyDown(e, index)}
                        onPaste={handlePaste}
                        aria-label={`Digit ${index + 1} of ${OTP_LENGTH}`}
                        autoComplete={index === 0 ? "one-time-code" : "off"}
                      />
                    ))}
                  </div>
                </div>
              </>
            )}

            {verificationStep === 1 && !regularUser && (
              <>
                <p className="signin-lead">
                  Enter the password for <strong>{username}</strong> to
                  continue.
                </p>

                {invalidPassword && (
                  <div
                    className="bl-alert bl-alert--danger"
                    role="alert"
                  >
                    <i
                      className="bl-alert__icon fas fa-circle-exclamation"
                      aria-hidden="true"
                    />
                    <div>That password is not correct.</div>
                  </div>
                )}

                <div className="bl-field">
                  <label
                    className="bl-field__label"
                    htmlFor="signin-password"
                  >
                    Password
                  </label>
                  {/* An eye toggle inside the field, not a checkbox below it:
                      the control now sits where the thing it acts on is. */}
                  <div className="signin-pw">
                    <input
                      id="signin-password"
                      autoFocus
                      className="bl-input"
                      type={showPassword ? "text" : "password"}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="current-password"
                      required
                      aria-required="true"
                    />
                    <button
                      type="button"
                      className="signin-pw__toggle"
                      onClick={() => setShowPassword((v) => !v)}
                      aria-label={
                        showPassword ? "Hide password" : "Show password"
                      }
                      aria-pressed={showPassword}
                      title={showPassword ? "Hide password" : "Show password"}
                    >
                      <i
                        className={showPassword ? "fas fa-eye-slash" : "fas fa-eye"}
                        aria-hidden="true"
                      />
                    </button>
                  </div>
                </div>
              </>
            )}

            <div className="signin-actions">
              {verificationStep > 0 && (
                <button
                  type="button"
                  className="signin-back"
                  onClick={back}
                >
                  <i
                    className="fas fa-arrow-left"
                    aria-hidden="true"
                  />
                  Back
                </button>
              )}
              <button
                type="submit"
                className="bleep-btn-report bleep-btn-report--sm bleep-btn-report--block"
                disabled={loading}
                aria-busy={loading ? "true" : "false"}
              >
                {loading ? (
                  <>
                    <i
                      className="fas fa-circle-notch fa-spin"
                      aria-hidden="true"
                    />
                    Working
                  </>
                ) : (
                  <>
                    {verificationStep === 0 ? "Send me a code" : "Sign in"}
                    <i
                      className="fas fa-arrow-right"
                      aria-hidden="true"
                    />
                  </>
                )}
              </button>
            </div>
          </form>

          <p className="signin-legal">
            By continuing you accept our{" "}
            <Link
              to="/termsofservice"
              target="_blank"
            >
              Terms of Service
            </Link>{" "}
            and{" "}
            <Link
              to="/privacypolicy"
              target="_blank"
            >
              Privacy Policy
            </Link>
            .
          </p>
        </div>
      </div>

      {/* ------------------------------------------------------ Brand column */}
      {/* .bl-hero carries the token flip and the stacking context. The canvas
          is not decoration: the band's own --bl-bg is off-white, so without it
          the panel and its off-white type both disappear. */}
      <aside
        className="bl-hero signin-aside"
        aria-hidden="true"
      >
        <GradFlow
          className="bl-hero__gradflow"
          config={HERO_GRADFLOW_CONFIG}
        />
        <HeroBubbles variant="column" />
        <div className="signin-aside__copy">
          <p className="signin-aside__line">
            Report a fake product in under a minute. We take it to the brands
            and agencies who can act.
          </p>
        </div>
      </aside>
    </div>
  );
};

export default SignIn;
